import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CmmsPages } from '@app/pages/cmms/state/cmms.reducer';
import { TableType } from '@app/pages/cmms/types';
import { ICMMSColumn } from '@shared/ui-components/au-table-layout/utils/interfaces';
import { iconEnum } from '@app/configs/model/IconEnum';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { AuTableLayoutModule } from '@app/shared/ui-components';
import { MatButtonModule } from '@angular/material/button';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { OpenDtFromWorkspaceComponent } from '../shared-form-group-controls/cmms-open-dt-from-workspace/open-dt-from-workspace.component';

export interface ISectionConfig {
	name: string;
	newEntityText: string;
	existingEntityText: string;
	createNewComponent: CmmsPages;
	tableType: TableType;
	entityIcon: iconEnum;
	entityName: string;
	canOpenInDt: boolean;
}

export type SectionConfigMap = {
	[key in CmmsPages]?: ISectionConfig;
};

@Component({
	selector: 'entity-section[entityType]',
	templateUrl: './entity-section.component.html',
	styleUrls: ['./entity-section.component.scss'],
	imports: [
		MatExpansionModule,
		MatIconModule,
		CommonModule,
		MatMenuModule,
		AuTableLayoutModule,
		MatButtonModule,
		NgxTolgeeModule,
		OpenDtFromWorkspaceComponent,
	],
})
export class EntitySectionComponent {
	@Input() entityType: CmmsPages;
	@Input() set tableData(data: unknown[]) {
		this.dataForTable = data || [];
		this.isPanelOpen = !!this.dataForTable?.length;
	}
	@Input() tableColumns: ICMMSColumn[] = [];
	@Input() existOnly = false;
	@Output()
	addNewEntityItem = new EventEmitter<CmmsPages>();
	@Output() addExistingEntityItem = new EventEmitter<{
		page: CmmsPages;
		tableType: TableType;
	}>();
	@Output() unlink = new EventEmitter<{ data: unknown }>();
	@Output() viewInDtEvent = new EventEmitter<{ data: unknown }>();
	@Output() rowSelect = new EventEmitter<{
		data: unknown;
		entityType: CmmsPages;
	}>();

	dataForTable: unknown[] = [];
	isPanelOpen: boolean;

	readonly sectionConfig: SectionConfigMap = {
		[CmmsPages.editWorkOrder]: {
			name: 'work_orders',
			newEntityText: 'add_work_order',
			existingEntityText: 'work_order_dialog_add_existing',
			createNewComponent: CmmsPages.newWorkOrder,
			tableType: TableType.WORK_ORDER,
			entityIcon: iconEnum.cmms,
			entityName: 'work_order',
			canOpenInDt: true,
		},
		[CmmsPages.editIncident]: {
			name: 'incidents',
			newEntityText: 'add_incident',
			existingEntityText: 'add_existing_incident',
			createNewComponent: CmmsPages.newIncident,
			tableType: TableType.INCIDENTS,
			entityIcon: iconEnum.incident,
			entityName: 'incident',
			canOpenInDt: true,
		},
		[CmmsPages.editRequest]: {
			name: 'requests',
			newEntityText: 'add_new_request',
			existingEntityText: 'add_existing_request',
			createNewComponent: CmmsPages.newRequest,
			tableType: TableType.REQUESTS,
			entityIcon: iconEnum.request,
			entityName: 'request',
			canOpenInDt: false,
		},
	};

	addNew() {
		this.addNewEntityItem.next(this.sectionConfig[this.entityType].createNewComponent);
	}

	useExisting(event: Event) {
		event.stopPropagation();
		this.addExistingEntityItem.next({
			page: CmmsPages.existingEntities,
			tableType: this.sectionConfig[this.entityType].tableType,
		});
	}

	afterCollapseHandler() {
		this.isPanelOpen = false;
	}

	afterExpandHandler() {
		this.isPanelOpen = true;
	}

	viewEntity(event) {
		this.rowSelect.emit({ data: event, entityType: this.entityType });
	}
}
